import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, UserIcon } from '@heroicons/react/24/outline'

export default function BioPopup({ isOpen, onClose, member }) {
  if (!member) return null

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-2xl border border-gray-100 p-6 sm:p-8">
                {/* Close Button */}
                <button
                  type="button"
                  className="absolute right-4 top-4 rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                  {/* Photo / Avatar */}
                  <div className="shrink-0">
                    <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl overflow-hidden bg-gray-100 border-2 border-pacieh-green/20 shadow-md">
                      {member.imagePath || member.image ? (
                        <img
                          src={member.imagePath || member.image}
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-pacieh-blue/10 text-pacieh-blue">
                          <UserIcon className="w-12 h-12" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Header info */}
                  <div className="flex-1 text-center sm:text-left">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-pacieh-green/10 text-pacieh-green mb-2">
                      {member.title || member.role || 'PACIEH Leadership'}
                    </span>
                    <Dialog.Title as="h3" className="text-2xl font-bold font-display text-pacieh-blue">
                      {member.name}
                    </Dialog.Title>
                    {member.role && member.title && member.role !== member.title && (
                      <p className="text-sm font-medium text-gray-500 mt-1">
                        {member.role}
                      </p>
                    )}
                  </div>
                </div>

                {/* Biography Body */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                    Biography & Background
                  </h4>
                  <div className="text-gray-700 text-sm sm:text-base leading-relaxed space-y-3 max-h-[50vh] overflow-y-auto pr-2">
                    {member.bio ? (
                      <p>{member.bio}</p>
                    ) : (
                      <p className="italic text-gray-400">
                        Distinguished team member contributing to PACIEH's mission and initiatives across education and healthcare.
                      </p>
                    )}
                  </div>
                </div>

                {/* Close Button Footer */}
                <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-5 py-2.5 rounded-xl bg-pacieh-blue text-white text-xs font-semibold hover:bg-pacieh-green transition-colors"
                  >
                    Done
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}